import {Divider, Scene} from 'quickstart/components'
import {useBlockContext, useIsomorphicLayoutEffect} from 'quickstart/hooks'
import {actions, selectors} from 'quickstart/reducer'
import type {
  Admin,
  InnerBlock,
  BlockInfo,
  BlockMeta,
  LayoutBlock,
} from 'quickstart/types'
import {preload, tid} from 'quickstart/utils'
import {lazy, type LazyExoticComponent, Suspense, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {IS_BROWSER, IS_TEST, debugging, logger, stableJson} from 'tizra'

const log = logger('blocks/block')

interface InfoProps<C, G, N extends string> extends BlockMeta<C, G, N> {
  Admin: Admin<C> | LazyExoticComponent<Admin<C>>
  GlobalAdmin?: Admin<G> | LazyExoticComponent<Admin<G>>
  InnerBlock: InnerBlock<C>
  dividerAbove?: boolean
  dividerBelow?: boolean
  dividers?: boolean
  ssrEnabled?: boolean
  storybook?: any
  wrapperDiv?: boolean
}

const BlockDevtools = lazy(() =>
  import('./devtools.tsx').then(d => ({default: d.BlockDevtools})),
)

export const blockInfo = <
  C extends object | undefined,
  G extends object | undefined,
  N extends string,
>(
  infoProps: InfoProps<C, G, N>,
): BlockInfo<C, G, N> => {
  const {
    Admin,
    GlobalAdmin,
    InnerBlock,
    defaultConfig,
    defaultGlobalConfig,
    displayName,
    globalMigrate,
    migrate,
    dividers = true,
    dividerAbove = dividers,
    dividerBelow = dividers,
    name,
    ssrEnabled = true,
    title,
    wrapperDiv = true,
  } = infoProps

  const blog = log.logger(displayName)

  const LayoutBlock: LayoutBlock = ({
    blockId = (blog.warn('missing blockId'), 'mustBeStorybook'),
    config = (IS_TEST || blog.browser.warn('missing config'), undefined),
    sortKey = (blog.warn('missing sortKey'), undefined),
  }) => {
    const blog = log.logger(displayName)
    const context = useBlockContext()

    // Draw a divider above this block if:
    // - this block wants a divider
    // - there's a previous block
    // - the previous block wants a divider
    const prevInfos = useSelector(selectors.getPrevInfos)
    const prevInfo = prevInfos[blockId]
    const divider = !!(dividerAbove && prevInfo?.dividerBelow)

    blog.debug?.('DIVIDER', displayName, divider, {
      blockId,
      sortKey,
      prevInfo: prevInfo && {
        displayName: prevInfo.displayName,
        dividerBelow: prevInfo.dividerBelow,
      },
      dividerAbove,
    })

    // We can't dispatch actions during render, so defer registration with
    // the Redux store to an effect. This means that blocks are never
    // registered with the store in SSR, but that's fine--we don't currently
    // save and inject the preloaded state between SSR and CSR anyway.
    const dispatch = useDispatch()
    const enter = useCallback(() => {
      // GlobalBlock and ThemeBlock have sortkey=0, others have sortkey>=1.
      if (sortKey) {
        dispatch(
          actions.enter(blockId, {
            displayName,
            dividerAbove,
            dividerBelow,
            sortKey,
          }),
        )
      }
    }, [blockId, dispatch, sortKey])
    useIsomorphicLayoutEffect(enter, [enter])

    // If the block uses Scene to disappear entirely for lack of content,
    // then remove the block from prevBlocks so that it won't confuse blocks
    // above and below for their divider calculation. (A block that wants to
    // use Scene internally without affecting prevBlocks can use <Scene
    // root>)
    const exit = useCallback(() => actions.exit(blockId), [blockId])
    const handleChange = useCallback(
      (v: any) => (v ? enter() : exit()),
      [enter, exit],
    )

    const migratedConfig = migrate({config, context})
    const blockDevtoolsEnabled = IS_BROWSER && debugging()

    let result = (
      <Scene onChange={handleChange}>
        {({show}: {show: boolean}) => (
          <>
            {show && divider && <Divider />}
            <InnerBlock config={migratedConfig} />
          </>
        )}
      </Scene>
    )

    if (wrapperDiv) {
      result = (
        // This is where data-testid="block" is applied for tests.
        <div
          className={`quickstart-${name}-block`}
          style={{
            // Ensure space for BlockDevtools button so they don't overlap
            // vertically if block renders empty.
            ...(blockDevtoolsEnabled && {minHeight: '24px'}),
          }}
          {...tid('block')}
        >
          {result}
        </div>
      )
    }

    return (
      <>
        {blockDevtoolsEnabled && (
          <Suspense fallback={null}>
            <BlockDevtools
              blockId={blockId}
              displayName={displayName}
              name={name}
              rawConfig={config}
              migratedConfig={migratedConfig}
              globalConfig={
                name in context.globalConfig
                  ? globalMigrate({context})
                  : undefined
              }
              blockContext={context}
              dividers={{
                above: dividerAbove,
                below: dividerBelow,
                prev: prevInfo && {
                  displayName: prevInfo.displayName,
                  below: prevInfo.dividerBelow,
                },
                sortKey,
              }}
            />
          </Suspense>
        )}
        {result}
      </>
    )
  }

  // InnerBlock is a React.lazy() component supplied by each block, and
  // renderToString() can't suspend, so make sure the server starts loading it
  // as soon as the bundle is evaluated.
  preload(InnerBlock as any)

  const info: BlockInfo<C, G, N> = {
    Admin,
    LayoutBlock,
    GlobalAdmin,

    displayName,
    dividerAbove,
    dividerBelow,
    globalMigrate,
    migrate,
    name,
    ssrEnabled,
    title,
    wrapperDiv,

    // Production code shouldn't need these, but they're useful for stories and
    // tests.
    defaultConfig,
    defaultGlobalConfig,

    // defaultConfigJson is used by layout_palettes_quickstartblocks.ftl to save
    // the default config for a block into the DB on first insert from palette
    // into a slot. That way an old block doesn't sprout new behavior
    // unexpectedly--it should always go through the block's migrate() function
    // to make a proper judgment.
    defaultConfigJson: defaultConfig
      ? stableJson(defaultConfig, {indent: 2})
      : '',
  }

  return info
}

export * from '../components'
export * from '../hooks'
export * from '../styled-components/system'
export * from '../utils'
export * from './components/Background'
export * from './components/Browse'
export * from './ComponentWrapper'
